import { motion, AnimatePresence } from "motion/react";
import { useCheckIn, type CheckInState } from "@/client/hooks/useCheckIn";
import { useToast } from "@/components/ds";
import { Spinner } from "@/components/ds";
import { MapPinIcon } from "@/icons/MapPinIcon";
import { ClockIcon } from "@/icons/ClockIcon";
import { CheckCircleIcon } from "@/icons/CheckCircleIcon";
import {
  springSnappy,
  springGentle,
  fadeSlideUp,
  staggerDelay,
} from "@/components/ds/motion";
import styles from "./ShiftCard.module.css";

interface CheckInRecord {
  readonly recordedAt: string;
  readonly verification: string;
}

interface ShiftCardProps {
  readonly id: string;
  readonly siteName: string;
  readonly siteAddress: string | null;
  readonly sitePostcode: string | null;
  readonly siteLatitude: number | null;
  readonly siteLongitude: number | null;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteNotes: string | null;
  readonly checkIn: CheckInRecord | null;
  readonly checkOut: CheckInRecord | null;
  readonly staggerIndex: number;
}

function formatTimeFriendly(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
}

function formatRecordedTime(isoString: string): string {
  const d = new Date(isoString);
  const h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
}

function mapsUrl(lat: number, lng: number): string {
  return `https://maps.google.com/?q=${lat},${lng}`;
}

function staticMapTileUrl(lat: number, lng: number): string {
  const zoom = 14;
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  );
  return `https://a.basemaps.cartocdn.com/light_all/${zoom}/${x}/${y}@2x.png`;
}

function ActionArea({
  state,
  siteName,
  onCheckIn,
  onConfirmOverride,
  onCancelOverride,
  onCheckOut,
  onRetry,
}: {
  state: CheckInState;
  siteName: string;
  onCheckIn: () => void;
  onConfirmOverride: () => void;
  onCancelOverride: () => void;
  onCheckOut: () => void;
  onRetry: () => void;
}) {
  switch (state.phase) {
    case "upcoming":
      return (
        <motion.button
          className={styles.checkInButton}
          onClick={onCheckIn}
          whileTap={{ scale: 0.95 }}
          transition={springSnappy}
        >
          Check in
        </motion.button>
      );

    case "locating":
      return (
        <button className={styles.checkInButton} disabled>
          <Spinner size="sm" />
          <span className={styles.locatingText}>Finding your location...</span>
        </button>
      );

    case "confirming":
      return (
        <motion.div
          className={styles.overridePrompt}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springGentle}
        >
          <p className={styles.overrideMessage}>
            You're {state.distanceMetres.toLocaleString()}m from {siteName}. Check in anyway?
          </p>
          <div className={styles.overrideButtons}>
            <button className={styles.cancelButton} onClick={onCancelOverride}>
              Cancel
            </button>
            <motion.button
              className={styles.checkInButton}
              onClick={onConfirmOverride}
              whileTap={{ scale: 0.95 }}
              transition={springSnappy}
            >
              Check in anyway
            </motion.button>
          </div>
        </motion.div>
      );

    case "checked_in":
      return (
        <>
          <motion.div
            className={styles.checkedInBanner}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springSnappy}
          >
            <CheckCircleIcon />
            <span className={styles.checkedInText}>
              Checked in at {formatRecordedTime(state.recordedAt)}
            </span>
          </motion.div>
          <motion.button
            className={styles.checkOutButton}
            onClick={onCheckOut}
            whileTap={{ scale: 0.95 }}
            transition={springSnappy}
          >
            Check out
          </motion.button>
        </>
      );

    case "checking_out":
      return (
        <>
          <div className={styles.checkedInBanner}>
            <CheckCircleIcon />
            <span className={styles.checkedInText}>Checked in</span>
          </div>
          <button className={styles.checkOutButton} disabled>
            <Spinner size="sm" />
            <span>Checking out...</span>
          </button>
        </>
      );

    case "completed":
      return (
        <div className={styles.completedBanner}>
          <CheckCircleIcon />
          <span className={styles.completedText}>
            Done! {formatRecordedTime(state.checkInAt)} to {formatRecordedTime(state.checkOutAt)}
          </span>
        </div>
      );

    case "error":
      return (
        <motion.button
          className={styles.checkInButton}
          onClick={onRetry}
          whileTap={{ scale: 0.95 }}
          transition={springSnappy}
        >
          Try again
        </motion.button>
      );
  }
}

export function ShiftCard({
  id,
  siteName,
  siteAddress,
  sitePostcode,
  siteLatitude,
  siteLongitude,
  startTime,
  endTime,
  siteNotes,
  checkIn,
  checkOut,
  staggerIndex,
}: ShiftCardProps) {
  const { state, checkIn: doCheckIn, confirmOverride, cancelOverride, checkOut: doCheckOut, retry } =
    useCheckIn({ id, checkIn, checkOut });
  const { toast } = useToast();

  const hasCoords = siteLatitude != null && siteLongitude != null;
  const isCompleted = state.phase === "completed";

  const handleCheckIn = () => {
    doCheckIn().catch(() => {
      toast("error", "Something went wrong. Please try again.");
    });
  };

  const handleConfirmOverride = () => {
    confirmOverride().catch(() => {
      toast("error", "Something went wrong. Please try again.");
    });
  };

  const handleCheckOut = () => {
    doCheckOut().catch(() => {
      toast("error", "Something went wrong. Please try again.");
    });
  };

  const hasLocation = hasCoords || siteAddress || sitePostcode;

  return (
    <motion.div
      className={`${styles.card} ${isCompleted ? styles.cardCompleted : ""}`}
      variants={fadeSlideUp}
      initial="initial"
      animate="animate"
      transition={{ ...springGentle, delay: staggerDelay(staggerIndex) }}
    >
      <div className={styles.cardBody}>
        <div className={styles.siteHeader}>
          <div className={styles.siteName}>{siteName}</div>

          {hasLocation && (
            <div className={styles.locationRow}>
              <MapPinIcon />
              <div className={styles.locationText}>
                {hasCoords ? (
                  <a
                    href={mapsUrl(siteLatitude!, siteLongitude!)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.addressLink}
                  >
                    {siteAddress ?? "Map link"}
                  </a>
                ) : (
                  siteAddress && <span className={styles.addressPlain}>{siteAddress}</span>
                )}
                {sitePostcode && (
                  <span className={styles.postcode}>{sitePostcode}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {hasCoords && (
          <a
            href={mapsUrl(siteLatitude!, siteLongitude!)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.mapLink}
            aria-label={`Map of ${siteName}`}
          >
            <img
              className={styles.mapImage}
              src={staticMapTileUrl(siteLatitude!, siteLongitude!)}
              alt=""
              loading="lazy"
            />
            <MapPinIcon className={styles.mapPin} />
          </a>
        )}

        <div className={styles.timeRow}>
          <ClockIcon />
          <span className={styles.timeText}>
            {formatTimeFriendly(startTime)} - {formatTimeFriendly(endTime)}
          </span>
        </div>

        {siteNotes && <div className={styles.notes}>{siteNotes}</div>}
      </div>

      <div className={styles.actions}>
        <AnimatePresence mode="wait">
          <ActionArea
            key={state.phase}
            state={state}
            siteName={siteName}
            onCheckIn={handleCheckIn}
            onConfirmOverride={handleConfirmOverride}
            onCancelOverride={cancelOverride}
            onCheckOut={handleCheckOut}
            onRetry={retry}
          />
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
